if (!customElements.get('customer-register-form')) {
  customElements.define(
    'customer-register-form',
    class CustomerRegisterForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('[type="submit"]');
        this.controller = null;
      }

      connectedCallback() {
        if (this.form) {
          this.form.addEventListener('submit', this.submitHandler.bind(this));
        }
      }

      /** @param {Event} e - The form submit event */
      async submitHandler(e) {
        e.preventDefault();
        if (!this.form) return;

        this.form.dataset.loading = 'true';

        try {
          /** @type {any} */
          const formElements = this.form.elements;
          const formData = {
            firstName: formElements['customer[firstname]'].value.trim(),
            lastName: formElements['customer[lastname]'].value.trim(),
            email: formElements['customer[email]'].value.trim(),
            password: formElements['customer[password]'].value,
            acceptsMarketing: formElements['customer[accepts_marketing]'].checked,
            birthday: formElements['property[birthday]'].value.trim(),
            gender: formElements['property[gender]'].value.trim(),
          };

          if (!formData.firstName || !formData.email || !formData.password) {
            throw new Error('Please fill in all required fields');
          }

          const customer = await this.createShopifyCustomer(formData);

          if (formData.acceptsMarketing) {
            await this.sendToKlaviyo({
              ...customer,
              birthday: formData.birthday,
              gender: formData.gender,
            });
          }

          await this.loginCustomer(customer.email, formData.password);
        } catch (/** @type {any} */ error) {
          this.showErrors([error.message]);

          this.form.dataset.loading = 'false';
        }
      }

      /**
       * @param {import('./types').Customer} formData - The form data
       * @returns {Promise<import('./types').Customer>} - The created customer
       */
      async createShopifyCustomer(formData) {
        const mutation = `
          mutation customerCreate($input: CustomerCreateInput!) {
            customerCreate(input: $input) {
              customer { id firstName lastName email acceptsMarketing }
              userErrors { field message }
            }
          }
        `;

        const response = await fetch('/api/2024-01/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': window.token,
          },
          body: JSON.stringify({
            query: mutation,
            variables: {
              input: {
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                password: formData.password,
                acceptsMarketing: formData.acceptsMarketing,
              },
            },
          }),
        });

        const { data, errors } = await response.json();

        if (errors) throw new Error(errors[0].message);

        if (data.customerCreate.userErrors?.length > 0) {
          throw new Error(data.customerCreate.userErrors[0].message);
        }

        return data.customerCreate.customer;
      }

      /**
       * @param {string} email - The customer's email
       * @param {string} password - The customer's password
       * @returns {Promise<void>} - A promise that resolves when the request is complete
       */
      async loginCustomer(email, password) {
        const request = await fetch(`${window.Shopify.routes.root}?sections=section-main-login`);

        if (!request.ok) {
          throw new Error(request.statusText);
        }

        const data = await request.json();

        if (request.ok) {
          const html = new DOMParser().parseFromString(data['section-main-login'], 'text/html');
          /** @type {any | null} */
          const form = html.querySelector('form');

          if (form) {
            form.elements['customer[email]'].value = email;
            form.elements['customer[password]'].value = password;

            form.style.display = 'none';
            document.body.appendChild(form);

            form.submit();
          }
        }
      }

      /**
       * @param {import('./types').Customer} customer - The customer to send to Klaviyo
       * @returns {Promise<void>} - A promise that resolves when the request is complete
       */
      async sendToKlaviyo(customer) {
        const payload = {
          data: {
            type: 'subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: customer.email,
                    first_name: customer.firstName,
                    last_name: customer.lastName,
                    external_id: customer.id,
                    properties: {
                      Birthday: customer.birthday,
                      Gender: customer.gender,
                    },
                    subscriptions: {
                      email: {
                        marketing: {
                          consent: 'SUBSCRIBED',
                        },
                      },
                    },
                  },
                },
              },
              custom_source: 'shopify_registration',
            },
            relationships: {
              list: {
                data: {
                  type: 'list',
                  id: 'YwGeYA',
                },
              },
            },
          },
        };

        const response = await fetch('https://a.klaviyo.com/client/subscriptions?company_id=WCbtTN', {
          method: 'POST',
          headers: {
            Accept: 'application/vnd.api+json',
            Revision: '2025-01-15',
            'Content-Type': 'application/vnd.api+json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.errors?.[0]?.detail || 'Unknown error');
        }
      }

      /** @param {string[]} errors - The errors to display */
      showErrors(errors) {
        const errorContainer = this.querySelector('.form-errors');

        if (!errorContainer) return;

        errorContainer.innerHTML = errors.map((/** @type {string} */ error) => `<li>${error}</li>`).join('');
      }
    },
  );
}
